/* eslint-disable @angular-eslint/no-input-rename */
/* eslint-disable @angular-eslint/no-output-native */
import { Component, Input, OnInit, ViewChild, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';
import { VideoPlayerViewModel } from '../video-player/VideoPlayer.viewmodel';

/**
 * @example 30 seconds video:
 * <app-youtube-player #video src="https://www.youtube.com/embed/w01V5FI03MQ" width="900" height="620" autoplay></app-youtube-player>
 */
@Component({
  selector: 'app-youtube-player',
  templateUrl: './youtube-player.component.html',
  styleUrls: ['./youtube-player.component.css']
})
export class YoutubePlayerComponent implements OnInit, VideoPlayerViewModel {
  private static youtubeApiLoading = false;
  private static youtubeApiLoaded  = false;

  static readonly DEFAULT_PLAYER_VARS: YT.PlayerVars = {
    enablejsapi:    1,
    autoplay:       0,
    fs:             1,
    origin:         location.origin,
    showinfo:       0,
    iv_load_policy: 3, // Hide
  };

  @ViewChild(YouTubePlayer, { static: false })
  public api!: YouTubePlayer;

  public get apiLoaded() {
    return YoutubePlayerComponent.youtubeApiLoaded;
  }

  /**
   * @param videoId taken from URLs, like: https://www.youtube.com/embed/<videoId>
   */
  @Input()
  public videoId?: string;

  @Input()
  public set src(value: string) {
    const src = value ? value.toLowerCase() : '';
    if (src.startsWith('https://www.youtube.com/embed/') || src.startsWith('https://youtube.com/embed/')) {
      this.videoId = value.split('?')[0].split('/').pop() || ''; // last segment
    }
  }

  @Input('width')
  public set inputWidth(value: number|string) {
    this.width = value === undefined ? value : Number(value);
  }

  @Input('height')
  public set inputHeight(value: number|string) {
    this.height = value === undefined ? value : Number(value);
  }

  @Input()
  public set autoplay(value: boolean|string) {
    this.playerVars.autoplay = value === true || value === 'true' || value === 'autoplay' || value === '' ? 1 : 0;
  }

  @Input()
  public style = '';

  @Input()
  public playerVars: YT.PlayerVars = YoutubePlayerComponent.DEFAULT_PLAYER_VARS;

  @Output()
  public loaded = new EventEmitter();

  @Output()
  public completed = new EventEmitter();

  public width?: number;
  public height?: number;
  public duration = 0; // sec
  public speed = 1; // normal
  public isLoaded = false;
  public isCompleted = false;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    // console.log('youtube player init', this.videoId);
    this.loadApi();
  }

  private loadApi() {
    if (!YoutubePlayerComponent.youtubeApiLoaded && !YoutubePlayerComponent.youtubeApiLoading) {
      YoutubePlayerComponent.youtubeApiLoading = true;

      // This code loads the IFrame Player API code asynchronously, according to the instructions at
      // https://developers.google.com/youtube/iframe_api_reference#Getting_Started
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(script);

      script.addEventListener('load', () => {
        YoutubePlayerComponent.youtubeApiLoading = false;
        YoutubePlayerComponent.youtubeApiLoaded = true;
        // console.log('youtube api loaded');
        this.cd.detectChanges();
      });
    }
  }

  onStateChange(event: YT.OnStateChangeEvent) {
    const state = this.findKey(event.data, YT.PlayerState);

    // console.log('youtube player state change', state || event);

    if (event.data === YT.PlayerState.PLAYING) {
      if (!this.isLoaded) {
        this.isLoaded = true;
        this.duration = this.api.getDuration();
        this.speed = this.api.getPlaybackRate();
        this.loaded.emit(event);
      }
    }

    if (event.data === YT.PlayerState.ENDED) {
      if (!this.isCompleted) {
        this.isCompleted = true;
        this.completed.emit();
      }
    }
  }

  onPlaybackRateChange(event: YT.OnPlaybackRateChangeEvent) {
    this.speed = event.data;
  }

  onError(event: YT.OnErrorEvent) {
    console.error('youtube player error', event);
  }

  private findKey(value: any, enumObj: object) {
    const entry = Object.entries(enumObj).find(pair => pair[1] === value);
    return entry ? entry[0] : null;
  }
}
